import {
	Autocomplete,
	Box,
	Checkbox,
	IconButton,
	Popper,
	TextField,
} from '@mui/material'
import { useQuery } from '@tanstack/react-query'
import { ReactComponent as NextIcon } from 'assets/arrow_close_logo.svg'
import { ReactComponent as SearchIcon } from 'assets/search.svg'
import { ReactComponent as CrossLogo } from 'assets/cross_close_logo.svg'
import CheckBoxActive from 'assets/icons/checkBoxActive'
import { ReactComponent as CheckBox } from 'assets/icons/checkBox.svg'
import { IGroupEntity } from 'models'
import { forwardRef, HTMLAttributes, useMemo, useState } from 'react'
import axAPI from 'httpConfig'
import { COLORS } from 'styles/colors'
import { TypographyPrimary } from 'ui/typography/TypographyPrimary'
import { TypographySecondary } from 'ui/typography/TypographySecondary'
import { TypographySecondary14 } from 'ui/typography/TypographySecondary14'

type GroupOption = {
	id: number
	title: string
	rationTitle: string
}

interface RationGroupsFieldProps {
	selectedIds: number[]
	onChange: (ids: number[]) => void
	error?: boolean
	showIntro?: boolean
	inputWidth?: string
	stacked?: boolean
}

const icon = <CheckBox />
const checkedIcon = <CheckBoxActive />

const GroupPopper = props => (
	<Popper
		{...props}
		modifiers={[
			...(props.modifiers ?? []),
			{
				name: 'offset',
				options: { offset: [0, 4] },
			},
		]}
	/>
)

const GroupListbox = forwardRef<HTMLUListElement, HTMLAttributes<HTMLElement>>(
	function GroupListbox(props, ref) {
		return (
			<Box
				component='ul'
				ref={ref}
				{...props}
				sx={{
					maxHeight: '250px !important',
					overflow: 'auto',
					padding: 0,
					margin: 0,
					'&::-webkit-scrollbar': {
						width: 4,
						WebkitAppearance: 'none',
					},
					'&::-webkit-scrollbar-button, &::-webkit-scrollbar-button:start:decrement, &::-webkit-scrollbar-button:end:increment':
						{
							display: 'none',
							width: 0,
							height: 0,
						},
					'&::-webkit-scrollbar-track': {
						backgroundColor: '#DBDBDB',
						borderRadius: 12,
					},
					'&::-webkit-scrollbar-thumb': {
						backgroundColor: COLORS.icon,
						borderRadius: 12,
					},
					'& .MuiAutocomplete-option, & .MuiAutocomplete-option.Mui-focused, & .MuiAutocomplete-option[aria-selected="true"], & .MuiAutocomplete-option[aria-selected="true"].Mui-focused, & .MuiAutocomplete-option:hover':
						{
							backgroundColor: 'transparent !important',
						},
				}}
			/>
		)
	},
)

const RationGroupsField = ({
	selectedIds,
	onChange,
	error = false,
	showIntro = true,
	inputWidth = '400px',
	stacked = false,
}: RationGroupsFieldProps) => {
	const [open, setOpen] = useState(false)
	const { data: groups = [], isLoading } = useQuery({
		queryKey: ['ration-assign-groups'],
		queryFn: async () => {
			const response = await axAPI({
				method: 'GET',
				url: 'groups/list',
			})
			return (response.data ?? []) as IGroupEntity[]
		},
		staleTime: 30000,
	})

	const options = useMemo<GroupOption[]>(
		() =>
			groups
				.map(group => ({
					id: group.id,
					title: group.title,
					rationTitle: group.rationtitle || '',
				}))
				.sort((left, right) => left.title.localeCompare(right.title, 'ru')),
		[groups],
	)
	const selectedOptions = options.filter(option =>
		selectedIds.includes(option.id),
	)

	const toggleGroup = (id: number) => {
		if (selectedIds.includes(id)) {
			onChange(selectedIds.filter(selectedId => selectedId !== id))
			return
		}
		onChange([...selectedIds, id])
	}

	const showError = error && selectedIds.length === 0

	return (
		<Box
			display='flex'
			flexDirection='column'
			gap='16px'
			width={stacked ? '100%' : undefined}
		>
			{showIntro && (
				<Box>
					<TypographyPrimary sx={{ fontWeight: 700, fontSize: '18px' }}>
						Применение в группах
					</TypographyPrimary>
					<TypographySecondary14
						sx={{ display: 'block', marginTop: '16px', whiteSpace: 'nowrap' }}
					>
						Выберите группы, для которых будет активирован рацион.
						<br />
						Текущие рационы в группах будут заменены.
					</TypographySecondary14>
				</Box>
			)}
			<Autocomplete
				sx={{ width: inputWidth }}
				multiple
				open={open}
				onOpen={() => setOpen(true)}
				onClose={() => setOpen(false)}
				options={options}
				loading={isLoading}
				value={selectedOptions}
				disableCloseOnSelect
				disableClearable
				ListboxComponent={GroupListbox}
				PopperComponent={GroupPopper}
				popupIcon={<NextIcon stroke={COLORS.icon} />}
				componentsProps={{
					popupIndicator: {
						disableRipple: true,
						sx: {
							padding: 0,
							marginRight: '8px',
							backgroundColor: 'transparent',
							'&:hover': { backgroundColor: 'transparent' },
						},
					},
				}}
				getOptionLabel={option => option.title}
				isOptionEqualToValue={(option, value) => option.id === value.id}
				filterOptions={(items, state) => {
					const query = state.inputValue.trim().toLowerCase()
					if (!query) return items
					return items.filter(item =>
						`${item.title} ${item.rationTitle}`.toLowerCase().includes(query),
					)
				}}
				onChange={(_, value) => onChange(value.map(option => option.id))}
				renderTags={() => null}
				renderOption={(props, option) => {
					const selected = selectedIds.includes(option.id)
					return (
						<li
							{...props}
							key={option.id}
							style={{
								padding: '4px 12px',
								minHeight: '50px',
								boxSizing: 'border-box',
								display: 'flex',
								alignItems: 'flex-start',
								gap: '8px',
								backgroundColor: 'transparent',
							}}
						>
							<Checkbox
								size='small'
								icon={icon}
								disableRipple
								checkedIcon={checkedIcon}
								style={{ padding: 0 }}
								checked={selected}
							/>
							<Box display='flex' flexDirection='column'>
								<TypographyPrimary>{option.title}</TypographyPrimary>
								<TypographySecondary14
									sx={{
										color: option.rationTitle
											? COLORS.secondaryFont
											: COLORS.error,
									}}
								>
									{option.rationTitle
										? `Рацион "${option.rationTitle}"`
										: 'Не назначен'}
								</TypographySecondary14>
							</Box>
						</li>
					)
				}}
				renderInput={params => (
					<TextField
						{...params}
						placeholder='Выберите группы'
						error={showError}
						helperText={showError ? 'Поле не может быть пустым' : undefined}
						inputProps={{
							...params.inputProps,
							placeholder: 'Выберите группы',
						}}
						InputProps={{
							...params.InputProps,
							startAdornment: open ? (
								<SearchIcon
									stroke={COLORS.icon}
									style={{ marginLeft: 8, flexShrink: 0 }}
								/>
							) : (
								params.InputProps.startAdornment
							),
						}}
						sx={{
							'& .MuiOutlinedInput-root': {
								borderRadius: '12px',
								height: '54px',
								paddingLeft: '8px',
								paddingTop: '0 !important',
								paddingBottom: '0 !important',
								backgroundColor: COLORS.white,
								flexWrap: 'nowrap',
								'& fieldset': {
									borderColor: showError ? COLORS.error : COLORS.inputBorder,
								},
								'&:hover fieldset': {
									borderColor: showError ? COLORS.error : COLORS.inputBorder,
								},
								'&.Mui-focused fieldset': {
									border: `1px solid ${
										showError ? COLORS.error : COLORS.inputBorder
									}`,
								},
							},
							'& .MuiOutlinedInput-input': {
								padding: '0 8px !important',
								fontSize: '14px',
							},
							'& .MuiOutlinedInput-input::placeholder': {
								color: COLORS.secondaryFont,
								opacity: 1,
								fontSize: '14px',
							},
						}}
					/>
				)}
			/>
			{selectedOptions.length > 0 && (
				<Box display='flex' flexDirection='column' gap='12px'>
					<Box
						display='flex'
						flexDirection='column'
						alignItems='flex-start'
						gap={stacked ? '16px' : '12px'}
					>
						{(stacked
							? selectedOptions.map(option => [option])
							: Array.from(
									{ length: Math.ceil(selectedOptions.length / 3) },
									(_, rowIndex) =>
										selectedOptions.slice(rowIndex * 3, rowIndex * 3 + 3),
								)
						).map(row => (
							<Box
								key={row.map(option => option.id).join('-')}
								display='flex'
								gap='12px'
								maxWidth='100%'
							>
								{row.map(option => (
									<Box
										key={option.id}
										sx={{
											display: 'flex',
											alignItems: 'center',
											justifyContent: 'space-between',
											width: 'fit-content',
											maxWidth: '100%',
											gap: '16px',
											padding: stacked ? '8px 12px 8px 16px' : '12px 16px',
											borderRadius: '12px',
											backgroundColor: '#F4F4F4',
											boxSizing: 'border-box',
										}}
									>
										<Box>
											<TypographyPrimary
												sx={{
													fontWeight: stacked ? 400 : 600,
													lineHeight: '18px',
												}}
											>
												{option.title}
											</TypographyPrimary>
											<TypographySecondary14
												sx={{
													fontSize: '13px',
													lineHeight: '18px',
													marginTop: '2px',
													color: COLORS.black,
												}}
											>
												Текущий рацион:{' '}
												<Box
													component='span'
													sx={{
														color: option.rationTitle
															? COLORS.black
															: COLORS.error,
													}}
												>
													{option.rationTitle || 'Не назначен'}
												</Box>
											</TypographySecondary14>
										</Box>
										<IconButton
											size='small'
											onClick={() => toggleGroup(option.id)}
											sx={{ padding: '2px' }}
										>
											<CrossLogo width={16} height={16} />
										</IconButton>
									</Box>
								))}
							</Box>
						))}
					</Box>
				</Box>
			)}
			<TypographySecondary
				sx={stacked ? { marginTop: '16px', fontSize: '12px' } : undefined}
			>
				Выбрано групп:&nbsp;&nbsp;{selectedIds.length}
			</TypographySecondary>
		</Box>
	)
}

export default RationGroupsField
